/**
 * MarketplaceActivationPage.jsx
 *
 * Azure Marketplace SaaS Landing Page.
 * URL recebida do Partner Center: /marketplace/activate?token=<opaque_token>
 *
 * Fluxo:
 * 1. Extrai `token` da query string
 * 2. POST /api/marketplace/resolve  → detalhes da subscription
 * 3. Mostra card com plano, offer, empresa do comprador
 * 4. Usuário confirma → POST /api/marketplace/activate
 * 5. Redireciona para /dashboard ou /governance/overview conforme plano
 *
 * Esta página é STANDALONE (sem Layout, sem sidebar) — registrada fora de
 * ProtectedAppRoutes para receber o token antes de autenticação local.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AZURE } from '../services/api';

// ── Design Tokens ────────────────────────────────────────────────────────────
const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

// ── Language detection ────────────────────────────────────────────────────────
const LANG = typeof navigator !== 'undefined' && navigator.language?.startsWith('en') ? 'en' : 'pt';

const T = {
  pt: {
    verifying: 'Verificando sua assinatura…',
    verifyingSub: 'Aguarde enquanto validamos seu token com a Microsoft.',
    activateTitle: 'Ativar Assinatura',
    activateSub: 'Confirme os detalhes abaixo antes de prosseguir.',
    planLabel: 'Plano Selecionado',
    subId: 'ID da Assinatura',
    subName: 'Nome',
    offer: 'Oferta',
    licenses: 'Quantidade de Licenças',
    buyerEmail: 'E-mail do Comprador',
    buyerTenant: 'Tenant do Comprador',
    confirmBtn: 'Confirmar e Ativar',
    cancelBtn: 'Cancelar',
    orgTitle: 'Configurar sua Organização',
    orgSub: 'Informe o nome da sua empresa ou organização para ativar o acesso.',
    orgLabel: 'Nome da organização',
    orgPlaceholder: 'Ex: Clube Atlético São Paulo',
    orgHint: 'Este nome será usado para identificar sua organização na plataforma.',
    orgCreating: 'Criando organização…',
    orgContinue: 'Continuar e Ativar',
    backBtn: 'Voltar',
    activating: 'Ativando assinatura…',
    activatingSub: 'Configurando seu acesso. Não feche esta janela.',
    doneTitle: 'Assinatura Ativada!',
    doneSub: 'Seu acesso está configurado. Redirecionando para o painel…',
    errorTitle: 'Erro na Ativação',
    retryBtn: 'Tentar Novamente',
    supportLink: 'Contatar Suporte',
    supportEmail: 'suporte@vukz.ai',
    tokenMissing: 'Token de ativação não encontrado na URL. Verifique o link recebido.',
    activateFailed: 'Ativação falhou.',
    contactSubject: 'Erro Marketplace Ativação',
  },
  en: {
    verifying: 'Verifying your subscription…',
    verifyingSub: 'Please wait while we validate your token with Microsoft.',
    activateTitle: 'Activate Subscription',
    activateSub: 'Please confirm the details below before proceeding.',
    planLabel: 'Selected Plan',
    subId: 'Subscription ID',
    subName: 'Name',
    offer: 'Offer',
    licenses: 'Quantity of Licenses',
    buyerEmail: "Buyer's Email",
    buyerTenant: "Buyer's Tenant",
    confirmBtn: 'Confirm & Activate',
    cancelBtn: 'Cancel',
    orgTitle: 'Set Up Your Organization',
    orgSub: 'Enter your company or organization name to activate access.',
    orgLabel: 'Organization name',
    orgPlaceholder: 'E.g.: Atletico Club São Paulo',
    orgHint: 'This name will be used to identify your organization on the platform.',
    orgCreating: 'Creating organization…',
    orgContinue: 'Continue & Activate',
    backBtn: 'Back',
    activating: 'Activating subscription…',
    activatingSub: 'Setting up your access. Do not close this window.',
    doneTitle: 'Subscription Activated!',
    doneSub: 'Your access is ready. Redirecting to your dashboard…',
    errorTitle: 'Activation Error',
    retryBtn: 'Try Again',
    supportLink: 'Contact Support',
    supportEmail: 'support@vukz.ai',
    tokenMissing: 'Activation token not found in the URL. Please verify the link you received.',
    activateFailed: 'Activation failed.',
    contactSubject: 'Marketplace Activation Error',
  },
}[LANG];

// ── Plano → nome legível (matches backend VALID_PLANS in routes_marketplace.py) ─
const PLAN_LABELS = {
  // Oferta 1: VUKZ Sports Intelligence
  'sports-starter':          'Sports Starter',
  'sports-club':             'Club Intelligence',
  'sports-federation':       'Federation Intelligence',
  // Oferta 2: VUKZ Sports Operating System
  'sg-starter':              'Sports OS – Starter',
  'sg-professional':         'Sports OS – Professional',
  'sg-enterprise':           'Sports OS – Enterprise',
  // Legacy v1
  'founder-starter':         'Governance OS – Founder Starter',
  'startup-pro':             'Governance OS – Startup Pro',
  'business-enterprise':     'Governance OS – Business Enterprise',
  'starter-sports':          'Sports Starter',
  'club-performance':        'Club Intelligence',
  'federation-enterprise':   'Federation Intelligence',
};

const OFFER_DESCRIPTIONS = {
  pt: {
    // Oferta 1
    'vukz-sports-intelligence':  'Pipeline de dados, rankings, análise de vídeo e insights de performance com IA. Desenvolvido para natação e atletismo, com arquitetura escalável para organizações multi-esporte.',
    // Oferta 2
    'vukz-sports-governance':    'Plataforma completa para organizações esportivas: inteligência esportiva + governança operacional. Dados, gestão, compliance e IA em um só produto.',
    // Legacy
    'vukz-governance-os':        'Governança corporativa, compliance e gestão de dados para sua organização.',
    'vukz-sports-intelligence-os': 'Pipeline de dados esportivos com rankings e insights de performance em tempo real.',
  },
  en: {
    // Offer 1
    'vukz-sports-intelligence':  'Data pipeline, rankings, video analysis and AI-powered performance insights. Built initially for swimming and athletics, with a scalable architecture for multi-sport organizations.',
    // Offer 2
    'vukz-sports-governance':    'Complete platform for sports organizations: sports intelligence + operational governance. Data, management, compliance and AI in a single product.',
    // Legacy
    'vukz-governance-os':        'Corporate governance, compliance and data management for your organization.',
    'vukz-sports-intelligence-os': 'Sports data pipeline with rankings and real-time performance insights.',
  },
};

// ── Helpers visuais ──────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    Subscribed:   ['bg-green-100 text-green-700', 'Ativo'],
    Suspended:    ['bg-yellow-100 text-yellow-700', 'Suspenso'],
    Unsubscribed: ['bg-red-100 text-red-700', 'Cancelado'],
    PendingFulfillmentStart: ['bg-blue-100 text-blue-700', 'Aguardando Ativação'],
  };
  const [cls, label] = map[status] || ['bg-gray-100 text-gray-600', status];
  return (
    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${cls}`}>
      {label}
    </span>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-800 text-right max-w-xs break-words">{value}</span>
    </div>
  );
}

// ── Componente Principal ─────────────────────────────────────────────────────
export default function MarketplaceActivationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';

  const [stage, setStage] = useState('resolving'); // resolving | confirm | institution | activating | done | error
  const [subscription, setSubscription] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [institutionId, setInstitutionId] = useState(null);
  const [institutionLoading, setInstitutionLoading] = useState(false);

  // ── Step 1: resolve token ──────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setErrorMsg(T.tokenMissing);
      setStage('error');
      return;
    }

    let cancelled = false;

    async function resolveToken() {
      try {
        const { data } = await AZURE.post('/marketplace/resolve', { marketplace_token: token });
        if (cancelled) return;

        if (!data.success) {
          throw new Error(data.error || 'Falha ao resolver o token.');
        }

        setSubscription(data.data);
        setStage('confirm');
      } catch (err) {
        if (cancelled) return;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          'Erro ao contatar o serviço. Tente novamente em instantes.';
        setErrorMsg(msg);
        setStage('error');
      }
    }

    resolveToken();
    return () => { cancelled = true; };
  }, [token]);

  // ── Step 2: provision tenant (cria institution se não existe) ────────────────
  async function handleProvisionTenant() {
    if (!subscription || !institutionName.trim()) return;
    setInstitutionLoading(true);
    try {
      const { data } = await AZURE.post('/marketplace/provision-tenant', {
        subscription_id: subscription.id,
        institution_name: institutionName.trim(),
      });
      if (!data.success) throw new Error(data.error || 'Erro ao criar organização.');
      setInstitutionId(data.data.institution_id);
      setStage('activating');
      await _doActivate(data.data.institution_id);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || (LANG === 'en' ? 'Failed to create organization.' : 'Erro ao criar organização.');
      setErrorMsg(msg);
      setStage('error');
    } finally {
      setInstitutionLoading(false);
    }
  }

  // ── Step 3: activate ─────────────────────────────────────────────────────────
  async function handleActivate() {
    // Verifica se já temos institution_id; se não, vai para step de provisioning
    if (!institutionId) {
      setStage('institution');
      return;
    }
    setStage('activating');
    await _doActivate(institutionId);
  }

  async function _doActivate(instId) {
    if (!subscription) return;
    try {
      const { data } = await AZURE.post('/marketplace/activate', {
        subscription_id: subscription.id,
        institution_id: instId,
        plan_id: subscription.planId,
      });

      if (!data.success) throw new Error(data.error || 'Ativação falhou.');

      setStage('done');
      const isGovernance = (subscription.offerId || '').includes('governance') && !(subscription.offerId || '').includes('sports');
      setTimeout(() => {
        navigate(isGovernance ? '/governance/overview' : '/feed', { replace: true });
      }, 2500);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || T.activateFailed;
      setErrorMsg(msg);
      setStage('error');
    }
  }

  // ── Render helpers ─────────────────────────────────────────────────────────
  const sub = subscription || {};
  const planLabel = PLAN_LABELS[sub.planId] || sub.planId || '—';
  const offerDesc = (OFFER_DESCRIPTIONS[LANG] || OFFER_DESCRIPTIONS.pt)[sub.offerId] || '';
  const buyerEmail = sub.purchaser?.emailId || sub.beneficiary?.emailId || '—';
  const buyerTenant = sub.purchaser?.tenantId || '—';

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 50%, #fefce8 100%)' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 mb-8">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
          style={{ background: BRAND_GREEN }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M3 7l9-4 9 4v10l-9 4-9-4V7z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
          </svg>
        </div>
        <div>
          <div className="font-extrabold text-xl" style={{ color: BRAND_GREEN }}>VUKZ Platform</div>
          <div className="text-xs text-gray-500">Azure Marketplace</div>
        </div>
      </div>

      <div className="w-full max-w-lg">
        {/* ── RESOLVING ── */}
        {stage === 'resolving' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 text-center">
            <div className="flex justify-center mb-4">
              <svg className="animate-spin w-10 h-10" style={{ color: BRAND_GREEN }} viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-700">{T.verifying}</h2>
            <p className="text-sm text-gray-500 mt-1">{T.verifyingSub}</p>
          </div>
        )}

        {/* ── CONFIRM ── */}
        {stage === 'confirm' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            {/* Header */}
            <div className="h-2" style={{ background: `linear-gradient(90deg, ${BRAND_GREEN}, ${BRAND_GOLD})` }} />
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-extrabold" style={{ color: BRAND_GREEN }}>
                {T.activateTitle}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {T.activateSub}
              </p>
            </div>

            {/* Detalhes */}
            <div className="p-6">
              {/* Plano */}
              <div
                className="rounded-xl p-4 mb-5"
                style={{ background: '#f0fdf4', border: `1px solid ${BRAND_GREEN}30` }}
              >
                <div className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: BRAND_GREEN }}>
                  {T.planLabel}
                </div>
                <div className="text-lg font-bold text-gray-900">{planLabel}</div>
                {offerDesc && <div className="text-sm text-gray-500 mt-1">{offerDesc}</div>}
                {sub.saasSubscriptionStatus && (
                  <div className="mt-2">
                    <StatusBadge status={sub.saasSubscriptionStatus} />
                  </div>
                )}
              </div>

              {/* Detalhes da subscription */}
              <div className="space-y-0">
                <InfoRow label={T.subId} value={sub.id} />
                <InfoRow label={T.subName} value={sub.name} />
                <InfoRow label={T.offer} value={sub.offerId} />
                <InfoRow label={T.licenses} value={sub.quantity?.toString()} />
                <InfoRow label={T.buyerEmail} value={buyerEmail} />
                <InfoRow label={T.buyerTenant} value={buyerTenant} />
              </div>
            </div>

            {/* Ações */}
            <div className="p-6 pt-0 flex gap-3">
              <button
                onClick={handleActivate}
                className="flex-1 py-3 rounded-xl text-white font-bold text-sm transition hover:opacity-90"
                style={{ background: BRAND_GREEN }}
              >
                {T.confirmBtn}
              </button>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="px-5 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm hover:bg-gray-50 transition"
              >
                {T.cancelBtn}
              </button>
            </div>
          </div>
        )}

        {/* ── INSTITUTION ── */}
        {stage === 'institution' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="h-2" style={{ background: `linear-gradient(90deg, ${BRAND_GREEN}, ${BRAND_GOLD})` }} />
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-extrabold" style={{ color: BRAND_GREEN }}>
                {T.orgTitle}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {T.orgSub}
              </p>
            </div>
            <div className="p-6">
              <div
                className="rounded-xl p-3 mb-5 text-sm text-gray-600"
                style={{ background: '#f0fdf4', border: `1px solid ${BRAND_GREEN}30` }}
              >
                <span className="font-semibold" style={{ color: BRAND_GREEN }}>{LANG === 'en' ? 'Plan:' : 'Plano:'}</span>{' '}
                {PLAN_LABELS[sub.planId] || sub.planId}
              </div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {T.orgLabel}
              </label>
              <input
                type="text"
                value={institutionName}
                onChange={e => setInstitutionName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && institutionName.trim().length >= 2 && handleProvisionTenant()}
                placeholder={T.orgPlaceholder}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-green-600"
                disabled={institutionLoading}
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-2">{T.orgHint}</p>
            </div>
            <div className="p-6 pt-0 flex gap-3">
              <button
                onClick={handleProvisionTenant}
                disabled={institutionLoading || institutionName.trim().length < 2}
                className="flex-1 py-3 rounded-xl text-white font-bold text-sm transition disabled:opacity-50"
                style={{ background: BRAND_GREEN }}
              >
                {institutionLoading ? T.orgCreating : T.orgContinue}
              </button>
              <button
                onClick={() => setStage('confirm')}
                disabled={institutionLoading}
                className="px-5 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm hover:bg-gray-50 transition"
              >
                {T.backBtn}
              </button>
            </div>
          </div>
        )}

        {/* ── ACTIVATING ── */}
        {stage === 'activating' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 text-center">
            <div className="flex justify-center mb-4">
              <svg className="animate-spin w-10 h-10" style={{ color: BRAND_GOLD }} viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-700">{T.activating}</h2>
            <p className="text-sm text-gray-500 mt-1">{T.activatingSub}</p>
          </div>
        )}

        {/* ── DONE ── */}
        {stage === 'done' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: '#dcfce7' }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke={BRAND_GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="text-xl font-extrabold" style={{ color: BRAND_GREEN }}>
              {T.doneTitle}
            </h2>
            <p className="text-sm text-gray-500 mt-2">
              {T.doneSub}
            </p>
            <div className="mt-4 h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-1 rounded-full"
                style={{ background: BRAND_GREEN, animation: 'progress 2.5s linear forwards' }}
              />
            </div>
            <style>{`@keyframes progress { from { width: 0 } to { width: 100% } }`}</style>
          </div>
        )}

        {/* ── ERROR ── */}
        {stage === 'error' && (
          <div className="bg-white rounded-2xl border border-red-200 shadow-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-50">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#dc2626" strokeWidth="2" />
                <path d="M12 8v4M12 16h.01" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <h2 className="text-xl font-extrabold text-red-700">{T.errorTitle}</h2>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{errorMsg}</p>
            <div className="mt-6 flex flex-col gap-2">
              <button
                onClick={() => window.location.reload()}
                className="w-full py-2.5 rounded-xl text-white font-bold text-sm"
                style={{ background: BRAND_GREEN }}
              >
                {T.retryBtn}
              </button>
              <a
                href={`mailto:${T.supportEmail}?subject=${encodeURIComponent(T.contactSubject)}`}
                className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm text-center hover:bg-gray-50 transition"
              >
                {T.supportLink}
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <p className="mt-8 text-xs text-gray-400 text-center">
        © {new Date().getFullYear()} VUKZ Platform — Powered by{' '}
        <a href="https://azuremarketplace.microsoft.com" className="underline hover:text-gray-600" target="_blank" rel="noreferrer">
          Azure Marketplace
        </a>
      </p>
    </div>
  );
}
